/**
 * @fileoverview Função utilitária para tratamento seguro de promises com resultados tipados
 *
 * Esta função fornece uma maneira padronizada de lidar com resoluções e rejeições de promises,
 * retornando um tipo união discriminada que garante o tratamento adequado de erros através da
 * verificação de tipos. Ela elimina a necessidade de blocos try/catch em todo o código e fornece
 * um padrão consistente de tratamento de erros.
 *
 * Benefícios:
 * - Tratamento de erros com segurança de tipos
 * - Padrão consistente de tratamento de erros
 * - Elimina repetição de try/catch
 * - Torna os casos de erro explícitos no tipo de retorno
 * - Garante que os erros não sejam acidentalmente ignorados
 *
 * @example
 * // Buscando dados do usuário
 * async function buscarUsuarioPorId(id) {
 *   const response = await fetch(`/api/usuarios/${id}`);
 *   return response.json();
 * }
 *
 * // Usando tryCatch para lidar com a promise (early return)
 * const { data, error } = await tryCatch(buscarUsuarioPorId('123'));
 *
 * if (error != null) {
 *   // Caso de erro - TypeScript sabe que resultado.error é do tipo Error
 *   console.error('Falha ao buscar usuário:', resultado.error.message);
 *   return
 * }
 *
 * // Caso de sucesso - TypeScript sabe que resultado.data é do tipo Usuario
 * console.log('Usuário encontrado:', resultado.data.nome);
 *
 * @example
 * // Com tipo de erro personalizado
 * class ErroApi extends Error {
 *   constructor(mensagem, public statusCode) {
 *     super(mensagem);
 *   }
 * }
 *
 * // Usando com async/await em um componente ou serviço
 * async function obterDadosUsuario() {
 *   const resultado = await tryCatch(buscarUsuarioPorId('123'));
 *
 *   if (resultado.error) {
 *     // Trata caso de erro
 *     return mostrarNotificacaoErro(resultado.error.message);
 *   }
 *
 *   // Trata caso de sucesso
 *   return processarDadosUsuario(resultado.data);
 * }
 *
 * @example
 * // Usando com encadeamento de Promises
 * tryCatch(buscarUsuarioPorId('123'))
 *   .then(resultado => {
 *     if (resultado.data) {
 *       atualizarInterface(resultado.data);
 *     } else {
 *       mostrarErro(resultado.error);
 *     }
 *   });
 */

/**
 * @template T
 * @typedef {Object} Success
 * @property {T} data - O resultado em caso de sucesso
 * @property {null} error - Sempre null em caso de sucesso
 */

/**
 * @template E
 * @typedef {Object} Failure
 * @property {null} data - Sem null em caso de erro
 * @property {E} error - O objeto de erro
 */

/**
 * @template T, E
 * @typedef {Success<T> | Failure<E>} Result
 */

/**
 * Wraps a promise to handle success and error cases in a type-safe way
 * @template T - O tipo do resultado de sucesso
 * @template [E=Error] - O tipo do erro, por padrão é Error
 * @param {Promise<T>} promise - A promise a ser encapsulada
 * @returns {Promise<Result<T, E>>} A promise que resolve com o resultado
 */
export async function tryCatch(promise) {
    try {
        const data = await promise;
        return { data, error: null };
    } catch (error) {
        return { data: null, error };
    }
}
